import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { getIdTokenClaims } from '../lib/auth.js';
import SectionCard from './SectionCard.jsx';
import Notice from './Notice.jsx';

/**
 * The Qlik Cloud offer (ENTRAID-348), at the top of the Qlik Cloud section.
 *  - The customer has Qlik Cloud (data.hasQlikCloud): how to host onGuard apps in it.
 *  - The customer has none: Qlik Cloud through IPC Global, and a short quote request form.
 * The request is the customer admin's alone (data.canRequestQuote, decided by the server); everyone else who can
 * read this section sees the same button, disabled, with the reason. Qlik is a trademark of QlikTech
 * International AB; the name is used as text only, never the logo.
 */

export const MAX_NOTE = 1000;
export const MAX_USERS = 100000;
const EMAIL_RE = /^[^\s@"<>,;]+@[^\s@"<>,;]+\.[^\s@"<>,;]+$/;
// Control and bidirectional formatting characters are never wanted in these fields (the server strips them too).
// Built from code points at runtime: raw invisible characters never appear in source.
const range = (a, b) => String.fromCharCode(a) + (b === undefined ? '' : `-${String.fromCharCode(b)}`);
const UNSAFE = new RegExp(`[${[range(0x00, 0x08), range(0x0b), range(0x0c), range(0x0e, 0x1f), range(0x7f, 0x9f), range(0x200b, 0x200f), range(0x202a, 0x202e), range(0x2066, 0x2069), range(0xfeff)].join('')}]`, 'g');
const clean = (s) => String(s ?? '').replace(UNSAFE, '');

export function validateQuote({ users, note, contactName, contactEmail }) {
  const errors = {};
  const n = Number(users);
  if (String(users).trim() === '' || !Number.isInteger(n) || n < 1 || n > MAX_USERS) errors.users = `Enter a whole number from 1 to ${MAX_USERS}.`;
  if (clean(contactName).trim().length === 0) errors.contactName = 'Enter your name.';
  else if (contactName.length > 100) errors.contactName = 'Use 100 characters or fewer.';
  const email = clean(contactEmail).trim();
  if (!EMAIL_RE.test(email) || email.length > 254) errors.contactEmail = 'Enter a valid email address.';
  if (note.length > MAX_NOTE) errors.note = `Use ${MAX_NOTE} characters or fewer.`;
  return errors;
}

export default function QlikOfferPanel({ data }) {
  if (data.hasQlikCloud === true) return <HostPanel />;
  if (data.hasQlikCloud === false) return <QuotePanel customerId={data.customerId} canRequest={data.canRequestQuote === true} />;
  return null;
}

function HostPanel() {
  return (
    <SectionCard title="Host onGuard apps in your Qlik Cloud" testId="qlik-host-panel" bodyClassName="space-y-2">
      <p className="text-sm text-ipc-ink">
        You can use your own Qlik Cloud to host apps built to consume onGuard for Entra ID data. Connect Qlik Cloud to your
        onGuard data with the steps on this page, then build or load your apps on top of it.
      </p>
      <p className="text-sm">
        <Link className="text-ipc-light hover:text-ipc-primary underline" to="/data-reference">Open the Data reference</Link>
        <span className="text-ipc-muted"> for every source, its fields and a Qlik Sense load script.</span>
      </p>
    </SectionCard>
  );
}

function QuotePanel({ customerId, canRequest }) {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(null);
  const claims = getIdTokenClaims() ?? {};
  const [form, setForm] = useState({
    users: '',
    note: '',
    contactName: clean(claims.name ?? [claims.given_name, claims.family_name].filter(Boolean).join(' ')),
    contactEmail: clean(claims.email ?? ''),
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    const found = validateQuote(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setBusy(true);
    setFailure('');
    try {
      const res = await api.requestQlikQuote(customerId, {
        users: Number(form.users),
        note: clean(form.note).trim(),
        contactName: clean(form.contactName).trim(),
        contactEmail: clean(form.contactEmail).trim(),
      });
      setDone(res);
      setOpen(false);
    } catch (err) {
      setFailure(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <SectionCard title="Get Qlik Cloud through IPC Global" testId="qlik-quote-panel" bodyClassName="space-y-3">
      <p className="text-sm text-ipc-ink">
        Qlik Cloud is the analytics platform. IPC Global, your Qlik partner, can provide Qlik Cloud and help you run the
        onGuard for Entra ID analytics apps on it.
      </p>

      {done ? (
        <Notice tone="good" role="status" titleAs="p" title="Thank you.">
          We sent your request to IPC Global{done.confirmation ? ' and a confirmation to your email address' : ''}. Someone will contact you soon.
        </Notice>
      ) : !open ? (
        <div>
          <button
            type="button"
            className="btn-primary text-sm"
            disabled={!canRequest}
            aria-describedby={canRequest ? undefined : 'qlik-quote-why'}
            onClick={() => setOpen(true)}
          >
            Request a quote
          </button>
          {!canRequest && <p id="qlik-quote-why" className="text-xs text-ipc-muted mt-2">Only your onGuard admin can request a quote.</p>}
        </div>
      ) : (
        <form onSubmit={submit} noValidate className="space-y-3 max-w-md" aria-label="Request a Qlik Cloud quote">
          <Field label="How many people will use Qlik Cloud?" error={errors.users}>
            <input type="number" inputMode="numeric" min="1" max={MAX_USERS} className="input w-full" value={form.users} onChange={set('users')} aria-invalid={!!errors.users} />
          </Field>
          <Field label="Your name" error={errors.contactName}>
            <input type="text" maxLength={100} autoComplete="name" className="input w-full" value={form.contactName} onChange={set('contactName')} aria-invalid={!!errors.contactName} />
          </Field>
          <Field label="Your email address" error={errors.contactEmail}>
            <input type="email" maxLength={254} autoComplete="email" className="input w-full" value={form.contactEmail} onChange={set('contactEmail')} aria-invalid={!!errors.contactEmail} />
          </Field>
          <Field label="Anything we should know? (optional)" error={errors.note} hint={`${form.note.length} of ${MAX_NOTE} characters`}>
            <textarea rows={4} maxLength={MAX_NOTE} className="input w-full" value={form.note} onChange={set('note')} aria-invalid={!!errors.note} />
          </Field>
          {failure && <Notice tone="bad" titleAs="p" title="The request was not sent">{failure}</Notice>}
          <div className="flex gap-3">
            <button type="submit" disabled={busy} className="btn-primary text-sm">
              {busy ? 'Sending...' : 'Send request'}
            </button>
            <button type="button" disabled={busy} onClick={() => setOpen(false)} className="btn-secondary text-sm">
              Cancel
            </button>
          </div>
        </form>
      )}
    </SectionCard>
  );
}

function Field({ label, error, hint, children }) {
  return (
    <label className="block text-sm text-ipc-ink">
      <span className="block mb-1">{label}</span>
      {children}
      {hint && <span className="block text-xs text-ipc-muted mt-1">{hint}</span>}
      {error && <span className="block text-xs text-red-700 mt-1" role="alert">{error}</span>}
    </label>
  );
}
